import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Settings, LogOut, Menu, X, ChevronDown, ChevronRight } from 'lucide-react'
import { useState } from 'react'
import { ThemeToggle } from './ui/ThemeToggle'

interface LayoutProps {
  children: React.ReactNode
}

interface MenuGroup {
  label: string
  icon: string
  items: { path: string; label: string; icon: string }[]
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({
    core: true,
    agu: true,
    system: false,
  })
  const location = useLocation()

  const isActive = (path: string) => location.pathname === path
  const isGroupActive = (group: MenuGroup) => group.items.some(item => location.pathname === item.path)

  const toggleGroup = (key: string) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const menuGroups: Record<string, MenuGroup> = {
    core: {
      label: 'Core',
      icon: '⚡',
      items: [
        { path: '/dashboard', label: 'Dashboard', icon: '📊' },
        { path: '/platforms', label: 'Platforms', icon: '🏢' },
        { path: '/plans', label: 'Plans', icon: '📋' },
        { path: '/subscriptions', label: 'Subscriptions', icon: '💳' },
        { path: '/payments', label: 'Payments', icon: '💰' },
        { path: '/crypto-payment', label: 'Crypto Payment', icon: '₿' },
      ],
    },
    agu: {
      label: 'AGU Points',
      icon: '🤖',
      items: [
        { path: '/agu', label: 'Overview', icon: '📈' },
        { path: '/agu/charge', label: 'Charge', icon: '💎' },
        { path: '/agu/escrow', label: 'Escrow', icon: '🔒' },
        { path: '/agu/staking', label: 'Staking', icon: '📊' },
        { path: '/agu/withdraw', label: 'Withdraw', icon: '💸' },
        { path: '/agu/analytics', label: 'Analytics', icon: '📉' },
      ],
    },
    system: {
      label: 'System',
      icon: '⚙️',
      items: [
        { path: '/api-key', label: 'API Keys', icon: '🔑' },
      ],
    },
  }

  const renderSidebar = () => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-border shrink-0">
        {sidebarOpen && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-xs">U3P</span>
            </div>
            <span className="font-bold text-foreground">Payment</span>
          </div>
        )}
        <button
          onClick={() => {
            setSidebarOpen(!sidebarOpen)
            setMobileOpen(false)
          }}
          className="p-1.5 hover:bg-muted rounded-lg transition"
        >
          {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Navigation Groups */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {Object.entries(menuGroups).map(([key, group]) => (
          <div key={key} className="mb-2">
            {/* Group Header */}
            {sidebarOpen ? (
              <button
                onClick={() => toggleGroup(key)}
                className={`w-full flex items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-md transition ${
                  isGroupActive(group) ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  <span>{group.icon}</span>
                  {group.label}
                </span>
                {expandedGroups[key] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            ) : (
              <div className="flex justify-center py-1 text-lg" title={group.label}>
                {group.icon}
              </div>
            )}

            {/* Group Items */}
            {(expandedGroups[key] || !sidebarOpen) && (
              <div className={sidebarOpen ? 'mt-0.5' : ''}>
                {group.items.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg mb-0.5 text-sm transition ${
                      isActive(item.path)
                        ? 'bg-primary/10 text-primary font-medium shadow-sm'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <span className="text-base">{item.icon}</span>
                    {sidebarOpen && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-border p-2 shrink-0">
        <Link
          to="/settings"
          onClick={() => setMobileOpen(false)}
          className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition ${
            isActive('/settings')
              ? 'bg-primary/10 text-primary'
              : 'text-muted-foreground hover:bg-muted'
          }`}
        >
          <Settings size={18} />
          {sidebarOpen && <span>Settings</span>}
        </Link>
        <Link
          to="/logout"
          onClick={() => setMobileOpen(false)}
          className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-muted transition mt-0.5"
        >
          <LogOut size={18} />
          {sidebarOpen && <span>Logout</span>}
        </Link>
      </div>
    </>
  )

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex ${
          sidebarOpen ? 'w-60' : 'w-16'
        } bg-card border-r border-border transition-all duration-200 flex-col`}
      >
        {renderSidebar()}
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border flex flex-col transform transition-transform duration-200 lg:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {renderSidebar()}
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="h-14 bg-card border-b border-border flex items-center justify-between px-4 lg:px-6 shrink-0">
          <div className="flex items-center gap-3">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-1.5 hover:bg-muted rounded-lg"
              onClick={() => setMobileOpen(true)}
            >
              <Menu size={20} />
            </button>
            <h1 className="text-lg font-bold text-foreground">
              U3P Payment Platform
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-sm">
                AD
              </div>
              <span className="hidden sm:inline text-sm font-medium text-muted-foreground">Admin</span>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  )
}
